/* eslint-disable no-case-declarations */
import { clsx } from "clsx";
import { type ScaleLinear } from "d3";
import {
  type Dispatch,
  type FC,
  type MouseEventHandler,
  type ReactNode,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { mergeProps, VisuallyHidden } from "react-aria";
import Measure from "react-measure";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import {
  AnimatedTooltip,
  DialogComponent,
  LoadingBar,
  LoadingSpinner,
  Popover,
  SymbolIcon,
  useHoverInteractive,
} from "widgets";
import { useLongLoading, useTooltip } from "widgets/hooks";

import { type Contig, type JobResult, type Range } from "@/graphql.ts";
import { updateLocalUserPref } from "@/redux/modules/auth.ts";
import {
  getAllVisibleTrackIds,
  getTrackId,
  type IGenomeBrowserState,
  type IGenomeViewerStateLayout,
  type ISortableTrack,
  type ITrackData,
  type ITrackDataMap,
  PIN_TRACK,
  viewHeightChanged,
  viewWidthChanged,
} from "@/redux/modules/genome-browser.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { type ReduxDispatch } from "@/redux/redux-utils.ts";
import { TRACK_TYPE } from "@/settings/settings.ts";
import { CoordinatesTrack } from "@/ui-modules/genome-viewer/coordinate-track/CoordinatesTrack.tsx";

import {
  type IExpressionOverlayTooltipData,
  type IFeatureTooltipData,
  TRACK_TOOLTIP_TYPE,
} from "../../app-utils/track-shapes/shape-feature.js";
import { type IBlastHspTooltipData, TrackBlastHspTooltip } from "../blast-result/HspShape.js";
import { useGenomeViewerData } from "./_mod.js";
import { BedsTrack } from "./bed-track/BedsTrack.js";
import { BlastHitTrackLoader } from "./BlastHitTrackLoader.js";
import {
  TrackAaFeatureTooltip,
  TrackBedTooltip,
  TrackExpressionTooltip,
  TrackFeatureTooltip,
  TrackNuFeatureTooltip,
} from "./features-track/FeatureTooltips.js";
import { type TooltipState } from "./features-track/FeatureTrack.js";
import { FeatureTrackLoader } from "./features-track/FeatureTrackLoader.js";
import {
  type IVariantProfileTooltipData,
  type IVariantSymbolTooltipData,
  TrackVariantProfileTooltip,
  TrackVariantSymbolTooltip,
} from "./features-track/VariantTooltips.js";
import { FlagTrack } from "./flag-track/FlagTrack.js";
import { GenomeViewerAxis } from "./GenomeViewerAxis.js";
import { GenomeViewerInteractiveSurface } from "./GenomeViewerInteractiveSurface.js";
import { type INgsReadsTooltip } from "./ngs-track/NgsReadsTrackLoader.js";
import { NgsReadsTrackTooltip } from "./ngs-track/NGSReadsTrackTooltip.js";
import { NgsTrack } from "./ngs-track/NgsTrack.js";
import { ProfileTrackLoader } from "./ProfileTrackLoader.js";
import { SequenceTrack } from "./sequence-track/SequenceTrack.js";
import style from "./styles/GenomeViewerTracksList.module.scss";
import trackStyle from "./styles/Track.module.scss";
import { VariantTrackLoader } from "./variants-track/VariantTrackLoader.js";

export const ShowMessageOverTrack: FC<{ message: string; onClick?: MouseEventHandler }> = ({ message, onClick }) => {
  const textStyle = onClick ? { cursor: "pointer", color: "#2988cc" } : {};
  return (
    <div className={trackStyle.track_message} style={textStyle} onClick={onClick}>
      {message}
    </div>
  );
};

export interface GenomeViewerData {
  layout: IGenomeViewerStateLayout;
  range: Range;
  contig: Contig;
  normalize: ScaleLinear<number, number>;
  baseWidth: number;
  isLoading: IGenomeBrowserState["isLoading"];
}

/**
 * A visualization component that renders a list of genome browser tracks.
 *
 * The component consists of a single SVG containing a top axis of the currently visible base pair range. The
 * rendering of the actual genomic feature tracks is delegated to the FeatureTrack component, which in turn
 * creates a group node for each track. In addition, for each tick in the top axis, the component draws
 * a tick line from top to bottom in the background. Note that layouting of the genomic features in the track,
 * called pileup creation, is done before the actual rendering in order to determine the total height of the SVG.
 *
 * UI Functions:
 *  - tracks can be dragged to the left and right to change the currently visible range
 *  - shift-click drag allows the user to zoom into the selection rectangle defined by the drag operation
 *  - ctrl-mousewheel allows for zooming in and out, while maintaining the center of the visible range
 *
 * Total SVG dimension overview (horizontal and vertical):
 * ( this.props.boxModel.width is the measured value of svgWidth )
 *
 * Horizontal direction:
 * | <---------------------- svgWidth ---------------------> |
 * | padding.left ¦ labelWidth ¦ canvasWidth ¦ padding.right |
 *
 * Vertical direction:
 * | <----------------------------------------------- svgHeight ------------------------------------> |
 * |            | <--------------------------------- trackHeight -----------------------------------> |
 * | axisHeight | padding.top | (lanes * featureHeight) + (lanes - 1) * lanePadding) ¦ padding.bottom |
 *
 *
 * created on 11/25/2016
 */

interface IGenomeViewerTracksListProps {
  jobResult?: JobResult; // if the genome viewer is mounted from BLAST job result component
  handleCustomizeVariantsTrack: (trackType: string) => void; // method to call when the "customize" link of Variants track is clicked
  handleCustomizeNgsTrack: () => void;
  handleCustomizeBedTrack: () => void;
  handleCustomizeCoordinateTrack: () => void;
}

export type SetTooltipContent = Dispatch<SetStateAction<TooltipState> | null>;

const GenomeViewerTracksList: FC<IGenomeViewerTracksListProps> = (props: IGenomeViewerTracksListProps) => {
  const dispatch = useDispatch<ReduxDispatch>();

  const showFlags = useSelector<IAppState, boolean>((state) => state.auth.session?.params.showFlagsTrack, shallowEqual);
  const tracks = useSelector<IAppState, ISortableTrack[]>((state) => state.genomeBrowser.tracks, shallowEqual);
  const trackDataMap = useSelector<IAppState, ITrackDataMap>((state) => state.genomeBrowser.trackDataMap, shallowEqual);
  const visibleTracks = useMemo(() => tracks.filter((track: ISortableTrack) => !track.hidden), [tracks]);
  const range = useSelector<IAppState, Range>((state) => state.genomeBrowser.range, shallowEqual);
  const width = useSelector<IAppState, number>((state) => state.genomeBrowser.layout.trackWidth, shallowEqual);
  const { normalize, layout } = useGenomeViewerData();
  const isLoading = useMemo<boolean>(
    () =>
      getAllVisibleTrackIds(visibleTracks)
        .map((trackId: string) => trackDataMap[trackId])
        .reduce((isLoading: boolean, trackData: ITrackData) => isLoading || trackData?.isLoading, false),
    [visibleTracks, trackDataMap],
  );
  const isLoadingForLong = useLongLoading(isLoading);

  const contig = useSelector<IAppState, Contig>((state) => state.genomeBrowser.contig, shallowEqual);

  const getTrack = useCallback(
    (track: ISortableTrack) => {
      const trackId = getTrackId(track);

      switch (track.type) {
        case TRACK_TYPE.SEQUENCE:
          return <SequenceTrack trackName={"Sequence"} contigId={contig?.id} range={range} />;
        case TRACK_TYPE.GENE:
        case TRACK_TYPE.TRANSCRIPT_LOCATION:
        case TRACK_TYPE.PROTEIN_LOCATION:
        case TRACK_TYPE.NUFEATURE:
        case TRACK_TYPE.AAFEATURE:
        case TRACK_TYPE.MARKER_NUS:
          return (
            <FeatureTrackLoader
              key={`feature-track-${trackId}`}
              track={track}
              scale={normalize}
              setTooltipContent={setTooltipContent}
            />
          );
        case TRACK_TYPE.VARIANT:
        case TRACK_TYPE.VCF_VARIANT:
          return (
            <VariantTrackLoader
              key={`variant-track-${trackId}`}
              track={track}
              scale={normalize}
              handleCustomizeVariantsTrack={props.handleCustomizeVariantsTrack}
              setTooltipContent={setTooltipContent}
            />
          );
        case TRACK_TYPE.BLAST_ALIGNMENT:
          return (
            <BlastHitTrackLoader
              key={`feature-track-${trackId}`}
              jobResult={props.jobResult}
              scale={normalize}
              track={track}
              setTooltipContent={setTooltipContent}
            />
          );
        case TRACK_TYPE.PROFILE:
          return <ProfileTrackLoader track={track} scale={normalize} key={`feature-track-${getTrackId(track)}`} />;
        case TRACK_TYPE.NGS:
          return (
            <NgsTrack
              key={`feature-track-${trackId}`}
              track={track}
              scale={normalize}
              handleCustomize={props.handleCustomizeNgsTrack}
              setTooltipContent={setTooltipContent}
            />
          );
        case TRACK_TYPE.BED:
          return (
            <BedsTrack
              key={`feature-track-${trackId}`}
              track={track}
              scale={normalize}
              handleCustomize={props.handleCustomizeBedTrack}
              setTooltipContent={setTooltipContent}
            />
          );
        case TRACK_TYPE.COORDINATE:
          return (
            <CoordinatesTrack
              key={`feature-track-${trackId}`}
              track={track}
              scale={normalize}
              handleCustomize={props.handleCustomizeCoordinateTrack}
              setTooltipContent={setTooltipContent}
            />
          );
      }

      return null;
    },
    [normalize, props.handleCustomizeNgsTrack, props.handleCustomizeVariantsTrack, props.jobResult],
  );

  // -- Tooltip
  const tooltipTimeoutId = useRef(-1);
  // We must keep the tooltip anchor and move it offscreen or the tooltip will be visible on the top-left of the viewport
  const invisibleTooltipState: TooltipState = {
    style: { width: 0, height: 0, top: -10000, left: -10000 },
    content: undefined,
  };
  const [tooltip, setTooltip] = useState<TooltipState>(invisibleTooltipState);
  const loadingTooltipUid = useRef<string>(undefined);

  const triggerRef = useRef(null);
  const { state, overlayProps, hoverProps } = useHoverInteractive({
    isOpen: tooltip.isLoading || tooltip.shapeHovered || false,
  });

  const setTooltipContent: SetTooltipContent = useCallback((tooltipState) => {
    window.clearTimeout(tooltipTimeoutId.current);

    if (tooltipState) {
      tooltipTimeoutId.current = window.setTimeout(() => {
        setTooltip((previous) => {
          if (typeof tooltipState === "function") {
            // When it's a function, we want to mutate from the previous state.
            return tooltipState(previous);
          } else {
            // getContent is a Promise, so we just mutate the loading
            return tooltipState.getContent ? { ...tooltipState, isLoading: true } : tooltipState;
          }
        });

        if (typeof tooltipState !== "function") {
          if (tooltipState.getContent) {
            // Then we load the content
            loadingTooltipUid.current = tooltipState.uid;

            void tooltipState.getContent().then((data) => {
              if (tooltipState.uid === loadingTooltipUid.current) {
                // Same request, we now update the content
                setTooltip((prevState) => ({ ...prevState, content: data, isLoading: false }));
              }
            });
          }
        }

        // The timeout has unfortunately to be equal or higher than
        // the tooltip disappearance timeout, which is 500ms.
      }, 550);
    }
  }, []);

  const closeTooltip = () => {
    window.clearTimeout(tooltipTimeoutId.current);
    setTooltip(invisibleTooltipState);
  };

  useEffect(() => {
    setTooltipContent(null);
  }, [range, width]);

  const tooltipContent = useMemo<ReactNode>(() => {
    if (tooltip.content == undefined) return null;

    switch (tooltip.type) {
      case TRACK_TOOLTIP_TYPE.NGS:
        const { alignment, segment, index } = tooltip.content as INgsReadsTooltip;
        return <NgsReadsTrackTooltip alignment={alignment} segment={segment} index={index} />;
      case TRACK_TOOLTIP_TYPE.EXPRESSION_OVERLAY:
        const { expression, expressionColoringConditionNameLookup: lookup } =
          tooltip.content as IExpressionOverlayTooltipData;
        return <TrackExpressionTooltip expression={expression} expressionColoringConditionNameLookup={lookup} />;
      case TRACK_TOOLTIP_TYPE.VARIANT:
        const { pileupVariant, groups: variantGroups } = tooltip.content as IVariantSymbolTooltipData;
        return <TrackVariantSymbolTooltip pileupVariant={pileupVariant} groups={variantGroups} />;
      case TRACK_TOOLTIP_TYPE.VARIANT_PROFILE:
        const { variant, mutantGenome } = tooltip.content as IVariantProfileTooltipData;
        return <TrackVariantProfileTooltip variant={variant} mutantGenome={mutantGenome} />;
      case TRACK_TOOLTIP_TYPE.BLAST_HIT:
        const { pileupHsp } = tooltip.content as IBlastHspTooltipData;
        return <TrackBlastHspTooltip pileupHsp={pileupHsp} />;
      case TRACK_TOOLTIP_TYPE.FEATURE:
        const { pileupFeature, groups, expressionColoringConditionNameLookup } = tooltip.content as IFeatureTooltipData;
        switch (pileupFeature.feature.type) {
          case TRACK_TYPE.AAFEATURE:
            return <TrackAaFeatureTooltip tooltipData={pileupFeature} groups={groups} />;
          case TRACK_TYPE.NUFEATURE:
            return <TrackNuFeatureTooltip tooltipData={pileupFeature} groups={groups} />;
          case TRACK_TYPE.BED:
            return <TrackBedTooltip tooltipData={pileupFeature} />;
          default:
            return (
              <TrackFeatureTooltip
                tooltipData={pileupFeature}
                groups={groups}
                expressionColoringConditionNameLookup={expressionColoringConditionNameLookup}
              />
            );
        }
    }

    return null;
  }, [tooltip]);

  return (
    <>
      <div
        style={{
          ...tooltip.style,
          position: "fixed",
          pointerEvents: "none", // Usage of triggerProps is useless
          zIndex: 10,
        }}
        ref={triggerRef}
      />
      <Popover
        key={tooltip?.uid}
        {...overlayProps}
        triggerRef={triggerRef}
        isNonModal
        state={state}
        placement="top"
        offset={5}
      >
        <DialogComponent
          domProps={hoverProps}
          contentStyle={{ padding: 0 }}
          width={tooltip.type !== TRACK_TOOLTIP_TYPE.NGS ? 480 : 800}
        >
          {tooltipContent}
        </DialogComponent>
      </Popover>
      <GenomeViewerInteractiveSurface closeTooltip={closeTooltip}>
        {isLoadingForLong && <LoadingSpinner inCenter leftMargin={layout.headerWidth} />}
        <div className={style.tracks_loading_bar}>
          <LoadingBar isLoading={isLoading} debounceStart={200} />
        </div>
        <VisuallyHidden>
          {/* Make sure the fonts are pre-loaded before the rendering */}
          <div style={{ fontFamily: "Roboto Flex" }}>Roboto Flex</div>
          <div style={{ fontFamily: "Roboto Flex Mono" }}>Roboto Flex Mono</div>
        </VisuallyHidden>
        <GenomeViewerAxis />
        <Measure
          scroll
          onResize={(rect) => {
            if (layout.viewHeight !== rect.scroll.height) {
              dispatch(viewHeightChanged(rect.scroll.height));
            }
          }}
        >
          {({ measureRef: heightRef }) => (
            <div className={style.tracks} ref={heightRef}>
              <div className={clsx(style.track_wrapper, style.pinned_track)}>
                {showFlags && <FlagTrack contigId={contig?.id} range={range} headerWidth={layout.headerWidth} />}
                {
                  visibleTracks
                    .filter((track) => track.pinned == PIN_TRACK.TOP)
                    .map(getTrack)
                    .filter((e) => !!e) // Remove tracks without proper trackElement (i.e. hits track enabled without job result in context)
                }
              </div>
              <Measure
                scroll
                onResize={(rect) => {
                  if (layout.viewWidth !== rect.scroll.width) {
                    dispatch(viewWidthChanged(rect.scroll.width));
                  }
                }}
              >
                {({ measureRef: widthRef }) => (
                  <div ref={widthRef} className={clsx(style.track_wrapper, style.main_track)}>
                    {
                      visibleTracks
                        .filter((track) => track.pinned == undefined)
                        .map(getTrack)
                        .filter((e) => !!e) // Remove tracks without proper trackElement (i.e. hits track enabled without job result in context)
                    }
                  </div>
                )}
              </Measure>
              <div className={clsx(style.track_wrapper, style.pinned_track)}>
                {
                  visibleTracks
                    .filter((track) => track.pinned == PIN_TRACK.BOTTOM)
                    .map(getTrack)
                    .filter((e) => !!e) // Remove tracks without proper trackElement (i.e. hits track enabled without job result in context)
                }
              </div>
            </div>
          )}
        </Measure>
        <KeyboardShortcutsIcon />
      </GenomeViewerInteractiveSurface>
    </>
  );
};

export const KeyboardShortcutsIcon: FC = () => {
  const dispatch = useDispatch<ReduxDispatch>();
  const triggerRef = useRef<HTMLDivElement>(null);
  const { triggerProps, tooltipProps, state } = useTooltip(triggerRef, {});

  return (
    <>
      <div
        className={style.keyboard_shortcuts_help}
        ref={triggerRef}
        {...mergeProps(triggerProps, {
          onClick: () => dispatch(updateLocalUserPref({ showShortcutsHelp: true })),
        })}
      >
        <SymbolIcon icon={"keyboard"} style={{ color: "var(--light-background)" }} />
      </div>
      <AnimatedTooltip
        state={state}
        domProps={tooltipProps}
        targetRef={triggerRef}
        title={"Shortcuts"}
        shortcut={"ctrl+shift+?"}
        placement="left"
        offset={7}
      >
        Keyboard and mouse usage for the Genome Viewer
      </AnimatedTooltip>
    </>
  );
};

export { GenomeViewerTracksList };
